import type { Auth0Client } from '@auth0/auth0-spa-js'
import * as Sentry from '@sentry/react'
import { withTimeout } from './clearClientStorage'

/**
 * Upper bound on a single token acquisition.
 *
 * Even with the iframe fallback disabled (see `useRefreshTokensFallback` in `./auth.ts`), the SDK
 * can still stall: it serialises `getTokenSilently` behind a `localStorage`-backed lock, retrying
 * `acquireLock('auth0.lock.getTokenSilently', 5000)` ten times before giving up — up to 50s if
 * another tab is holding it. Because this call gates every GraphQL operation at context creation,
 * an unbounded wait means the whole app sits on its loading spinner and then errors (ENG-1190).
 *
 * Note what this bound can and cannot do. `withTimeout` resolves early; it does **not** abort the
 * underlying SDK call, and `getTokenSilently` caches its in-flight promise under
 * `clientId::audience::scope` until that call settles. So a later caller re-joins the *same* hung
 * promise and simply collects its own fresh timeout — the bound reports the stall and lets the app
 * react to it, it does not retry it. Recovery is the caller's job (`AuthorizeUser` re-prompts for
 * login), which is why #22376's timeout alone did not fix ENG-1190.
 */
export const AUTH_TOKEN_TIMEOUT_MS = 15000

export interface AuthTokenResult {
  token: string | null
  /** Whatever `getTokenSilently` threw, or `undefined` on success/timeout. */
  error?: any
  /** True when the request was cut off by `AUTH_TOKEN_TIMEOUT_MS` rather than failing. */
  timedOut?: boolean
}

/**
 * True when Auth0 rejected because the grant itself is gone, rather than because the request
 * couldn't complete. Auth0's SDK throws untyped shapes and the field carrying the code differs
 * by path (`GenericError.error`, a plain string, an `Error.message`), hence the three probes.
 *
 * The order matters and is not cosmetic. The original inline version in `AuthorizeUser` probed
 * `err?.includes('invalid_grant')` before `err?.error?.includes(...)`, so for the common object
 * shape it threw `TypeError: q.includes is not a function` and never reached the `err.error`
 * probe — the whole recovery path was dead code in production (Sentry PAINTSCOUT-APP-CQ7, 51
 * users). Optional-chaining the *call* (`?.includes?.()`) is what makes each probe safe.
 */
export function isInvalidGrant(error: unknown): boolean {
  const e = error as any
  return !!(
    e?.error?.includes?.('invalid_grant') ||
    e?.message?.includes?.('invalid_grant') ||
    e?.includes?.('invalid_grant')
  )
}

/**
 * The SDK's MissingRefreshTokenError extends GenericError with `error: 'missing_refresh_token'`
 * and message "Missing Refresh Token …". Same three probe shapes as `isInvalidGrant`, for the
 * same reason. The bare-string probe is defensive parity with `isInvalidGrant` — the SDK never
 * throws a raw string for this error, so it should never fire in practice.
 */
export function isMissingRefreshToken(error: unknown): boolean {
  const e = error as any
  return !!(
    e?.error?.includes?.('missing_refresh_token') ||
    e?.message?.includes?.('Missing Refresh Token') ||
    e?.includes?.('missing_refresh_token')
  )
}

/**
 * True when the grant is unrecoverable without an interactive login: the refresh token is dead
 * (`invalid_grant` — revoked family, expired family, rotation reuse) or gone entirely
 * (`missing_refresh_token`). Retrying either only produces the same failure, so these are the
 * cases worth an immediate re-login redirect — unlike a timeout, which is usually transient.
 */
export function isFatalAuthError(error: unknown): boolean {
  return isInvalidGrant(error) || isMissingRefreshToken(error)
}

/**
 * Builds the bounded, single-flight token getter for one `Auth0Client`.
 *
 * Each app passes its own client — `apps/app`/`apps/shared` and `apps/admin` are separate Auth0
 * applications with different client ids and token settings — so the `pending` promise below is
 * per-getter, which is what we want: collapsing across two different clients would hand one app
 * the other's token.
 *
 * @param auth the app's Auth0 client
 * @param label used in the timeout message so Sentry groups each app's stalls separately
 * @param opts.onAuthFailure called (once per settled attempt) when the failure is fatal per
 *   `isFatalAuthError` — a dead or missing refresh token that no retry can fix. Deliberately NOT
 *   called on timeout: a 15s stall is usually transient (network, the SDK's cross-tab lock), and
 *   redirecting a working user mid-edit on it would lose unsaved work. Boot-time recovery in
 *   `AuthorizeUser` keeps its broader any-missing-token behaviour.
 */
export function createAuthTokenGetter(
  auth: Pick<Auth0Client, 'getTokenSilently'>,
  label = 'getAuthToken',
  opts?: { onAuthFailure?: (result: AuthTokenResult) => void }
) {
  /**
   * A single in-flight token request shared by all concurrent callers.
   *
   * App startup fires a burst of GraphQL requests (appDocs, offlineQuotes, plus every page query),
   * and `AuthorizeUser` re-runs its init effect — rebuilding the client and re-firing the burst —
   * whenever `session.user_id` changes, i.e. on every account switch. Collapsing them into one
   * shared promise guarantees at most one token request per burst.
   *
   * The SDK de-duplicates concurrent `getTokenSilently` calls itself, so this is belt-and-braces
   * rather than the only guard — but it also collapses the surrounding work and gives us one place
   * to bound and report the outcome.
   *
   * Sequential calls are unaffected: `pending` clears once settled.
   */
  let pending: Promise<AuthTokenResult> | null = null

  const getAuthTokenResult = async (): Promise<AuthTokenResult> => {
    if (!pending) {
      pending = (async () => {
        try {
          // `cacheMode: 'on'`, always. This used to read `getIdTokenClaims()` and force
          // `cacheMode: 'off'` whenever the ID token had expired — which is every request made
          // more than five minutes after login, because the `PaintScout` Auth0 client issues a
          // 300-second ID token (`jwt_configuration.lifetime_in_seconds`). The token this
          // function returns is the *access* token, and the `API` resource server gives that a
          // 86400-second lifetime. So the old check threw away a token good for another 23 hours
          // and forced a refresh-token exchange, on a client configured for rotating refresh
          // tokens. That churn is the ENG-1190 mechanism: each forced exchange is a chance to
          // land on a dead grant, and a dead grant then stalls.
          //
          // 'on' is not "use a stale token": the SDK treats a cache entry as expired 60 seconds
          // early and refreshes when the access token genuinely needs it. Callers that need fresh
          // *claims* still ask explicitly via `authenticate({ ignoreCache: true })`.
          //
          // `withTimeout` resolves (to undefined) rather than rejecting, so this always reaches
          // `finally` and clears `pending` — a stalled request can never wedge every later caller
          // behind a promise that will not settle.
          const token = await withTimeout(auth.getTokenSilently({ cacheMode: 'on' }), AUTH_TOKEN_TIMEOUT_MS)

          if (typeof token === 'string') {
            return { token }
          }

          // Report the stall. A console warning alone is invisible in production, and this is the
          // signal we watch to confirm the fix held — it is how ENG-1190 was finally pinned down.
          // `warning` level, not `error`: the app recovers by re-prompting for login.
          console.warn(`${label} timed out after ${AUTH_TOKEN_TIMEOUT_MS}ms`)
          Sentry.captureMessage(`${label} timed out after ${AUTH_TOKEN_TIMEOUT_MS}ms`, {
            level: 'warning',
            tags: { message: 'Auth token timeout' }
          })
          return { token: null, timedOut: true }
        } catch (e) {
          // Resolve instead of rethrowing so callers can inspect the outcome and recover, rather
          // than every GraphQL operation rejecting at context creation with an untyped SDK error.
          console.warn(`${label} error`, e)

          const result: AuthTokenResult = { token: null, error: e }
          if (opts?.onAuthFailure && isFatalAuthError(e)) {
            try {
              opts.onAuthFailure(result)
            } catch (hookError) {
              // The hook is a side channel — it must never break this function's
              // resolve-with-a-result contract. Reported to Sentry, not just the console: a
              // throwing hook means the recovery redirect itself failed, and that must be
              // visible in production monitoring.
              console.warn(`${label} onAuthFailure hook threw`, hookError)
              Sentry.captureException(hookError, { tags: { message: 'onAuthFailure hook threw' } })
            }
          }
          return result
        } finally {
          pending = null
        }
      })()
    }

    return pending
  }

  const getAuthToken = async (): Promise<string | null> => {
    const { token } = await getAuthTokenResult()
    return token
  }

  return { getAuthToken, getAuthTokenResult }
}
